import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleButton } from "@/components/GoogleButton";
import { roleHome } from "@/lib/format";

const Login = () => {
  const { loginWithPassword, isAuthenticated, user, initializing } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  if (!initializing && isAuthenticated) {
    return <Navigate to={location.state?.from || roleHome(user.role)} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { ok, error, user: u } = await loginWithPassword(email.trim(), password);
    setSubmitting(false);
    if (!ok) {
      setError(error);
      toast.error(error || "Login failed");
      return;
    }
    toast.success(`Welcome back, ${u.full_name.split(" ")[0]}`);
    navigate(location.state?.from || roleHome(u.role), { replace: true });
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="login-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-brand-light text-brand flex items-center justify-center">
            <LogIn size={20} />
          </div>
          <h1 className="mt-4 font-display text-3xl font-semibold text-slate-900 tracking-tight">
            Welcome back
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Log in to save cars, track inquiries, and book appointments.
          </p>
        </div>

        <div className="mt-8 space-y-3">
          <GoogleButton label="Continue with Google" returnRoute="/account" />
          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            Google sign-in is for customers only. Dealers, BDC, and admins use email login below.
          </p>
        </div>

        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <span className="flex-1 h-px bg-slate-200" />
          OR
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1"
              data-testid="login-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-rose-600" data-testid="login-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold"
            data-testid="login-submit-button"
          >
            {submitting ? "Logging in…" : "Log in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          New to BeloMotors?{" "}
          <Link to="/signup" className="font-semibold text-brand hover:underline" data-testid="link-to-signup">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
